import * as React from "react";

import {
  PaginationParams,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import { Resources, useResource } from "Translations/Resources";

import { DetailColumn } from "Components/Shared/PrimaryDataTable/DetailColumn";
import { Dictionary } from "lodash";
import { EmployeeAvatar } from "Components/Shared/EmployeeAvatar";
import { EmployeeListDto } from "Api/Api";
import { FilterProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import { OrderByProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { formatDate } from "Utils/DateUtils";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

type Props = {
  employees: EmployeeListDto[];
  onPageChange: (page: number, rows: number) => void;
  isLoading: boolean;
  resetTablePage?: boolean;
  filter: Dictionary<FilterProps>;
  orderBy: OrderByProps<EmployeeListDto>;
  paginationParams?: PaginationParams | null;
  totalCount: number;
};

const StyledTableWrapper = styled.div`
  .name {
    max-width: 520px;
  }

  .action-buttons {
    display: flex;
    align-items: center;
  }

  thead {
    .filter-popup {
      .selectbox {
        .ibis-MuiInputBase-root {
          min-width: 190px;
        }
      }
    }
  }
`;

export const EmployeesTable: React.FunctionComponent<Props> = props => {
  const {
    employees,
    onPageChange,
    isLoading,
    resetTablePage,
    filter,
    orderBy,
    paginationParams,
    totalCount,
  } = props;
  const history = useHistory();
  const { t } = useResource();

  const [openAvatarModal, setOpenAvatarModal] = React.useState(false);
  const [rowClicked, setRowClicked] = React.useState(false);
  const [
    selectedEmployee,
    setSelectedEmployee,
  ] = React.useState<EmployeeListDto | null>(null);

  const onRowClick = (
    employee: EmployeeListDto,
    columnID: TableIDType<EmployeeListDto>,
  ) => {
    if (columnID === "name") {
      setSelectedEmployee(employee);
      handleRowClick();
      return;
    }

    goToDetail(employee);
  };

  const handleRowClick = () => {
    if (!openAvatarModal) {
      setRowClicked(true);
    }
  };

  const handleOpenAvatarModal = () => {
    if (!openAvatarModal) {
      setOpenAvatarModal(true);
    }
  };

  React.useEffect(() => {
    if (!openAvatarModal && rowClicked && selectedEmployee) {
      setRowClicked(false);
      goToDetail(selectedEmployee);
    }

    if (selectedEmployee && rowClicked) {
      setRowClicked(false);
    }
    // eslint-disable-next-line
  }, [selectedEmployee, openAvatarModal, rowClicked]);

  const goToDetail = (employee: EmployeeListDto) => {
    const employeeID = employee.employeeID;

    history.push(
      `/${t(Resources.Routes.Employees)}/${t(
        Resources.Routes.EmployeeDetail,
      )}/${employeeID}`,
    );
  };

  const getCell = (
    id: TableIDType<EmployeeListDto>,
    employee: EmployeeListDto,
  ) => {
    switch (id) {
      case "name":
        return (
          <StyledFlex alignItems={"center"} className={"name"}>
            <EmployeeAvatar
              firstName={employee.firstName}
              lastName={employee.lastName}
              isTheBoss={false}
              imgUrl={employee.profilePhotoUrl}
              size={"24px"}
              fontSize={"12px"}
              isEnabledModal={true}
              onClick={handleOpenAvatarModal}
              openAvatarModal={openAvatarModal && selectedEmployee === employee}
              setOpenAvatarModal={setOpenAvatarModal}
            />
            <span>{employee.name}</span>
          </StyledFlex>
        );

      case "positionName":
        return <div className={"position-name"}>{employee.positionName}</div>;

      case "isActive":
        return <div>{employee.isActive ? "Ano" : "Ne"}</div>;

      case "dateUpdated":
        return <div>{formatDate(employee.dateUpdated)}</div>;

      case "departments":
        return (
          <div>
            {employee.departments?.map(x => x.departmentName)?.join(", ")}
          </div>
        );

      case "detail":
        return (
          <DetailColumn
            url={`/${t(Resources.Routes.Employees)}/${t(
              Resources.Routes.EmployeeDetail,
            )}/${employee.employeeID}`}
          />
        );

      case "contactPersonFirstName":
        return (
          <div>
            {employee.contactPersonLastName} {employee.contactPersonFirstName}
          </div>
        );

      case "updatedByUserFirstName":
        return (
          <div>
            {employee.dateUpdated && employee.updatedByUserFirstName
              ? `${employee.updatedByUserLastName} ${employee.updatedByUserFirstName}`
              : "-"}
          </div>
        );

      default:
        return <div>-</div>;
    }
  };

  return (
    <StyledTableWrapper>
      <PrimaryDataTable
        isLoading={isLoading}
        rows={
          employees?.map(company => ({
            data: company,
            rowEntityID: "employeeID",
          })) ?? []
        }
        columns={[
          {
            id: "name",
            label: "Jména",
            width: "20%",
            filter: filter["name"],
            orderBy: orderBy,
          },
          {
            id: "positionName",
            label: "Pozice",
            width: "18%",
            filter: filter["positionName"],
            orderBy: orderBy,
          },
          {
            id: "contactPersonFirstName",
            label: "Kontaktní osoba",
            width: "15%",
            filter: filter["contactPersonName"],
            orderBy: orderBy,
          },
          {
            id: "departments",
            label: "Oddělení",
            width: "15%",
            filter: filter["departmentNames"],
          },
          {
            id: "dateUpdated",
            label: "Poslední aktualizace",
            width: "8%",
            orderBy: orderBy,
          },
          {
            id: "updatedByUserFirstName",
            label: "Provedl",
            filter: filter["updatedByUserName"],
            width: "15%",
            orderBy: orderBy,
          },
          {
            id: "isActive",
            label: "Aktivní",
            width: "5%",
            filter: filter["isActive"],
            orderBy: orderBy,
          },
          {
            id: "detail",
          },
        ]}
        getCell={getCell}
        onPageChange={onPageChange}
        totalCount={totalCount}
        onRowClick={onRowClick}
        getHeaderCell={column => <div>{column.label}</div>}
        resetTablePage={resetTablePage}
        paginationParams={paginationParams}
      />
    </StyledTableWrapper>
  );
};
